import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const CookiePolicy = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Cookie Policy - Informativa sui Cookie"
        description="Informativa sui cookie utilizzati da Mangimi Rubini. Scopri come utilizziamo i cookie e come gestire le tue preferenze."
        canonical="/cookie-policy"
      />
      <Navbar />

      <div className="pt-24 pb-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/" className="inline-block mb-6">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna alla Home
            </Button>
          </Link>

          <h1 className="text-4xl font-bold mb-8">Cookie Policy</h1>

          <div className="bg-white rounded-lg shadow-sm p-8 space-y-6">
            <section>
              <h2 className="text-2xl font-semibold mb-4">Cosa sono i Cookie</h2>
              <p className="text-gray-700 leading-relaxed">
                I cookie sono piccoli file di testo che i siti web visitati inviano al browser dell'utente,
                dove vengono memorizzati per essere poi ritrasmessi agli stessi siti alla visita successiva.
                I cookie sono utilizzati per diverse finalità, hanno caratteristiche diverse e possono essere
                utilizzati sia dal titolare del sito che si sta visitando, sia da terze parti.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Tipologie di Cookie Utilizzati</h2>

              <div className="space-y-6">
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="text-xl font-semibold mb-2">1. Cookie Tecnici Necessari</h3>
                  <p className="text-gray-700 mb-3">
                    Questi cookie sono essenziali per il corretto funzionamento del sito e non richiedono consenso.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-gray-50 rounded">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="px-4 py-2 text-left">Nome</th>
                          <th className="px-4 py-2 text-left">Finalità</th>
                          <th className="px-4 py-2 text-left">Durata</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t">
                          <td className="px-4 py-2 font-mono text-sm">mangimi-rubini-cookie-consent</td>
                          <td className="px-4 py-2">Memorizza la scelta sui cookie</td>
                          <td className="px-4 py-2">365 giorni</td>
                        </tr>
                        <tr className="border-t">
                          <td className="px-4 py-2 font-mono text-sm">mangimi-rubini-cookie-preferences</td>
                          <td className="px-4 py-2">Memorizza le preferenze granulari per categoria (localStorage)</td>
                          <td className="px-4 py-2">Persistente (fino a revoca)</td>
                        </tr>
                        <tr className="border-t">
                          <td className="px-4 py-2 font-mono text-sm">sb-*-auth-token</td>
                          <td className="px-4 py-2">localStorage tecnico di Supabase per l'autenticazione dell'area riservata (/login, /admin)</td>
                          <td className="px-4 py-2">Fino al logout / scadenza sessione</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="text-xl font-semibold mb-2">2. Servizi di Terze Parti (con consenso)</h3>
                  <p className="text-gray-700 mb-3">
                    La pagina contatti può caricare la <strong>mappa interattiva di Google Maps</strong>.
                    La mappa non viene caricata automaticamente: viene mostrato un segnaposto e l'embed
                    Google viene attivato solo dopo il tuo consenso (dal banner cookie o cliccando
                    "Carica mappa Google"). Al caricamento, Google può impostare cookie e ricevere il tuo
                    indirizzo IP.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-gray-50 rounded">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="px-4 py-2 text-left">Nome</th>
                          <th className="px-4 py-2 text-left">Provider</th>
                          <th className="px-4 py-2 text-left">Finalità</th>
                          <th className="px-4 py-2 text-left">Durata</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t">
                          <td className="px-4 py-2 font-mono text-sm">NID / cookie Google Maps</td>
                          <td className="px-4 py-2">Google LLC</td>
                          <td className="px-4 py-2">Funzionamento e preferenze della mappa</td>
                          <td className="px-4 py-2">Fino a 6 mesi</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-sm text-gray-600 mt-3">
                    <strong>Privacy Policy Google:</strong>{" "}
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      https://policies.google.com/privacy
                    </a>
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Non utilizziamo attualmente cookie di analisi statistica (es. Google Analytics)
                    né cookie di marketing/profilazione.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Come Gestire i Cookie</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Puoi gestire le tue preferenze sui cookie in diversi modi:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>
                  <strong>Tramite il banner cookie:</strong> alla prima visita del sito puoi accettare o rifiutare
                  i cookie/servizi non necessari, oppure personalizzare le tue preferenze per categoria.
                </li>
                <li>
                  <strong>In qualsiasi momento:</strong> puoi rivedere o revocare il consenso cliccando sul link
                  <strong>"Gestisci preferenze cookie"</strong> presente nel footer di ogni pagina, che riapre il
                  pannello di scelta.
                </li>
                <li>
                  <strong>Tramite le impostazioni del browser:</strong> puoi configurare il tuo browser per
                  bloccare o cancellare i cookie. Ecco i link alle guide dei principali browser:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>
                      <a
                        href="https://support.google.com/chrome/answer/95647"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Google Chrome
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://support.mozilla.org/it/kb/Gestione%20dei%20cookie"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Mozilla Firefox
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://support.apple.com/it-it/guide/safari/sfri11471/mac"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Safari
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://support.microsoft.com/it-it/windows/eliminare-e-gestire-i-cookie-168dab11-0753-043d-7c16-ede5947fc64d"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Microsoft Edge
                      </a>
                    </li>
                  </ul>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Nota:</strong> La disabilitazione dei cookie tecnici potrebbe compromettere
                il corretto funzionamento del sito.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Aggiornamenti</h2>
              <p className="text-gray-700 leading-relaxed">
                Questa Cookie Policy può essere aggiornata periodicamente. Ti invitiamo a consultarla
                regolarmente per essere sempre informato su come utilizziamo i cookie.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Contatti</h2>
              <p className="text-gray-700 leading-relaxed">
                Per qualsiasi domanda relativa alla nostra Cookie Policy, puoi contattarci:
              </p>
              <ul className="list-none text-gray-700 mt-3 space-y-1">
                <li><strong>Email:</strong> mangimirubini@gmail.com</li>
                <li><strong>Telefono:</strong> +39 346 625 7639</li>
                <li><strong>Indirizzo:</strong> Viale Umbria 21, 06063 Magione (PG)</li>
              </ul>
            </section>

            <section className="border-t pt-6">
              <p className="text-sm text-gray-500">
                Ultimo aggiornamento: 7 luglio 2026
              </p>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CookiePolicy;
